/**
 * XLSX raw-cell coercion (F4/F37): the ONE mapping from a SheetJS raw typed cell
 * value to the canonical ingest TEXT.
 *
 * Both XLSX read paths — the preflight sample (`read-headers.ts`) and the full
 * scrub pass (`parse-stream.ts`) — read `sheet_to_json({ raw: true })` with
 * `XLSX.read(..., { cellDates: true })` and map every cell through this ONE
 * function, so what the mapping screen previews is byte-identical to what
 * ingest stores. Reading the RAW stored value (never the formatted display
 * text) is what PRODUCT.md promises: a rate formatted `0.0%` ingests as its
 * full-precision `0.08375`, a balance formatted `#,##0` keeps its cents, a big
 * ID never goes scientific via display formatting, and date cells arrive as
 * unambiguous ISO — so the date-format must-ask never fires for XLSX.
 *
 * Coercion policy per raw type:
 *  - `string`  → as-is (leading/trailing trim happens downstream, same as CSV).
 *  - `number`  → `String(n)`: JS's shortest round-trip decimal, full precision
 *    (Excel numbers are float64, so this is lossless). NaN/Infinity → `''`.
 *  - `Date`    → ISO `YYYY-MM-DD` from the LOCAL date parts. SheetJS
 *    `cellDates` builds the Date from the serial in LOCAL time (local
 *    midnight), so local accessors recover the calendar date exactly; UTC
 *    accessors would shift the day in any UTC+ timezone. An invalid Date → `''`.
 *  - `boolean` → `TRUE` / `FALSE` (Excel's canonical spelling, matching what
 *    the old formatted-text path produced).
 *  - error / null / undefined / anything else → `''` (no value).
 */

/** Left-pad a number to `width` digits. */
function pad(n: number, width: number): string {
  return String(n).padStart(width, '0');
}

/** Coerce one SheetJS raw cell value to the canonical ingest text. */
export function coerceXlsxCellValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : '';
  }
  if (typeof value === 'boolean') return value ? 'TRUE' : 'FALSE';
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return '';
    // LOCAL parts: SheetJS cellDates Dates are local-midnight (see module doc).
    return `${pad(value.getFullYear(), 4)}-${pad(value.getMonth() + 1, 2)}-${pad(value.getDate(), 2)}`;
  }
  // Unknown object shape (SheetJS error cells already surface as '' under
  // sheet_to_json, but keep the coercion total): no value.
  return '';
}
